import * as _ from "lodash";

// Display-only mirror of the coupon minimum checked by calculate-final-price
// (get_valid_coupons). The server stays authoritative and reports the result
// in coupon.validMinOrder; these helpers only decide which amount to show.
//
// appliesToOrderGreaterThanByMode: { delivery?, takeaway?, dinein? }. A present
// key overrides appliesToOrderGreaterThan for that order mode, and an explicit
// 0 means no minimum for it. A missing key or map, a non-plain-object map, or a
// value that is null, "", whitespace only, "null", negative, not a number or
// numeric string, or not finite, is no override: the legacy
// appliesToOrderGreaterThan applies. Numeric strings are read with parseFloat.
// Same parsing as resolveModeOverride in calculate-final-price couponMinOrder.js.
//
// minOrderBasis "discount_items": the minimum counts only the cart lines that
// receive this coupon's item discount (appliesToCategoriesOrProducts). It only
// takes effect on percent or fixed coupons with a non-empty list.

export const COUPON_MIN_ORDER_MODES = ["delivery", "takeaway", "dinein"];

const I18N_PREFIX = "cart.cart-components.cart-active-coupons.";

export function getCouponMinOrderModeKey(
  deliveryMethod: string
): string | null {
  return _.includes(COUPON_MIN_ORDER_MODES, deliveryMethod)
    ? I18N_PREFIX + "minimum-order-" + deliveryMethod
    : null;
}

export function getCouponMinOrderModeOverride(
  coupon: any,
  deliveryMethod: string
): number | null {
  if (
    !coupon ||
    !_.includes(COUPON_MIN_ORDER_MODES, deliveryMethod) ||
    !_.isPlainObject(coupon.appliesToOrderGreaterThanByMode) ||
    !_.has(coupon.appliesToOrderGreaterThanByMode, deliveryMethod)
  ) {
    return null;
  }

  const value = coupon.appliesToOrderGreaterThanByMode[deliveryMethod];
  let parsedValue: number;

  if (typeof value === "number") {
    parsedValue = value;
  } else if (
    typeof value === "string" &&
    value.trim() !== "" &&
    value !== "null"
  ) {
    parsedValue = parseFloat(value);
  } else {
    return null;
  }

  return _.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
}

export function hasCouponMinOrderModeOverrides(coupon: any): boolean {
  return _.some(
    COUPON_MIN_ORDER_MODES,
    (mode) => getCouponMinOrderModeOverride(coupon, mode) !== null
  );
}

export function isCouponMinOrderDiscountItemsBasis(coupon: any): boolean {
  return !!(
    coupon &&
    coupon.minOrderBasis === "discount_items" &&
    (coupon.discountType === "percent" || coupon.discountType === "fixed") &&
    _.isArray(coupon.appliesToCategoriesOrProducts) &&
    coupon.appliesToCategoriesOrProducts.length
  );
}

// True when the coupon uses either new minimum field, so its minimum can
// change with the order mode or with the discounted products in the cart.
export function hasCouponMinOrderRules(coupon: any): boolean {
  return (
    hasCouponMinOrderModeOverrides(coupon) ||
    isCouponMinOrderDiscountItemsBasis(coupon)
  );
}

// Names of the categories/products whose lines count towards the minimum,
// joined for display. Empty unless the basis is "discount_items".
export function getCouponMinOrderItemNames(coupon: any): string {
  if (!isCouponMinOrderDiscountItemsBasis(coupon)) {
    return "";
  }

  return _.chain(coupon.appliesToCategoriesOrProducts)
    .map((item) => (item && _.isString(item.name) ? _.trim(item.name) : ""))
    .compact()
    .uniq()
    .value()
    .join(", ");
}

function formatMinOrderAmount(amount: number): string {
  return _.isInteger(amount) ? amount.toString() : amount.toFixed(2);
}

// Minimum for the given order mode, as shown next to the coupon.
// amount: null when the coupon has no minimum for that mode. With no override
// for the mode the legacy value is returned unchanged, exactly as it was shown
// before. modeKey: i18n key of the mode, set only when an override applies.
export function getCouponMinOrderDisplay(
  coupon: any,
  deliveryMethod: string
): { amount: string | null; modeKey: string | null; itemNames: string } {
  const display = { amount: null, modeKey: null, itemNames: "" };

  if (!coupon) {
    return display;
  }

  const override = getCouponMinOrderModeOverride(coupon, deliveryMethod);

  if (override !== null) {
    if (override > 0) {
      display.amount = formatMinOrderAmount(override);
      display.modeKey = getCouponMinOrderModeKey(deliveryMethod);
    }
  } else if (
    coupon.appliesToOrderGreaterThan &&
    coupon.appliesToOrderGreaterThan !== "0"
  ) {
    display.amount = coupon.appliesToOrderGreaterThan.toString();
  }

  if (display.amount) {
    display.itemNames = getCouponMinOrderItemNames(coupon);
  }

  return display;
}

// Per-mode minimums for the coupons list, which is not tied to an order mode.
// Empty when the coupon has no per-mode override, so the caller keeps showing
// the legacy single minimum. Only the modes the coupon is valid for are listed,
// and only those with a minimum.
export function getCouponMinOrderModesDisplay(
  coupon: any
): { modeKey: string; amount: string }[] {
  if (!hasCouponMinOrderModeOverrides(coupon)) {
    return [];
  }

  const restrictedModes = _.filter(
    COUPON_MIN_ORDER_MODES,
    (mode) => !!coupon[mode + "_only"]
  );
  const modes = restrictedModes.length
    ? restrictedModes
    : COUPON_MIN_ORDER_MODES;

  return _.compact(
    _.map(modes, (mode) => {
      const override = getCouponMinOrderModeOverride(coupon, mode);
      const amount =
        override !== null
          ? override
          : coupon.appliesToOrderGreaterThan
          ? parseFloat(coupon.appliesToOrderGreaterThan)
          : 0;

      if (!_.isFinite(amount) || !(amount > 0)) {
        return null;
      }

      return {
        modeKey: getCouponMinOrderModeKey(mode),
        amount: formatMinOrderAmount(amount),
      };
    })
  );
}

// Toast shown when the server reports validMinOrder false for a coupon:
// the existing message, followed by the minimum for the current order mode
// and, for the "discount_items" basis, the categories that count.
export function buildCouponMinOrderErrorMessage(
  coupon: any,
  deliveryMethod: string,
  translate: (key: string) => string
): string {
  const display = getCouponMinOrderDisplay(coupon, deliveryMethod);
  let message = translate(I18N_PREFIX + "coupon-requires-a-minimum-order");

  if (display.amount) {
    message = message + ": " + display.amount + "€";

    if (display.modeKey) {
      message = message + " (" + translate(display.modeKey) + ")";
    }

    if (display.itemNames) {
      message =
        message +
        ", " +
        translate(I18N_PREFIX + "minimum-order-only-from") +
        ": " +
        display.itemNames;
    }
  }

  return message;
}
